import {
  MessageCircle,
  Settings,
  Menu,
  X,
  BarChart3,
  Clock,
  AlertCircle,
  Files,
  BookOpen,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { UserMenu } from "./auth/UserMenu";
import { useAuth } from "context/AuthContext";
import { useState } from "react";

type SidebarProps = {
  onChatClick?: () => void;
  /** Stacked subscription / trial / WhatsApp banners (each ~2.25rem) — used to offset the mobile menu button. */
  bannerRows?: number;
  /** When already on Home, reopen the contact list (narrow layouts). */
  onOpenHomeContactList?: () => void;
};

export function Sidebar({
  onChatClick,
  bannerRows = 0,
  onOpenHomeContactList,
}: SidebarProps) {
  const location = useLocation();
  const { isImpersonating, lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMain = location.pathname === "/";
  const isSettings = location.pathname === "/settings";
  const isReports = location.pathname === "/reports";
  const isDailySummaries = location.pathname === "/daily-summaries";
  const isCases = location.pathname === "/cases";
  const isDocuments = location.pathname === "/documents";
  const isKnowledge = location.pathname === "/knowledge";

  const baseClass =
    "w-10 h-10 rounded-full flex items-center justify-center transition-colors";

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        onTouchEnd={(e) => {
          // Prevent default to avoid double-tap zoom and ensure click fires
          e.preventDefault();
          setIsMobileMenuOpen(!isMobileMenuOpen);
        }}
        style={{
          top: `calc(env(safe-area-inset-top, 0px) + 0.75rem + ${bannerRows * 2.25}rem + ${isMain ? 0.25 : 0}rem)`,
        }}
        className="lg:hidden fixed left-4 z-[60] flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
        aria-expanded={isMobileMenuOpen}
        aria-label={locale === "es" ? "Menú principal" : "Main menu"}
      >
        {isMobileMenuOpen ? (
          <X className="h-5 w-5 shrink-0 text-gray-600" strokeWidth={2} />
        ) : (
          <Menu className="h-5 w-5 shrink-0 text-gray-600" strokeWidth={2} />
        )}
      </button>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[99998]"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-[99999] transition-transform duration-300 ease-in-out lg:translate-x-0 lg:pointer-events-auto ${
          isMobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full pointer-events-none"
        } w-16 bg-white border-r border-gray-200 flex flex-col items-center py-4 space-y-6 ${
          isImpersonating ? "border-l-4 border-l-red-600" : ""
        }`}
      >
        <Link
          to="/"
          className={`${baseClass} ${
            isMain
              ? "bg-blue-500 text-white"
              : "bg-blue-100 hover:bg-blue-200 text-blue-600"
          }`}
          title={locale === "es" ? "Chats" : "Chats"}
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              onOpenHomeContactList?.();
            }
            closeMobileMenu();
            onChatClick?.();
          }}
        >
          <MessageCircle className="w-5 h-5" />
        </Link>

        <Link
          to="/reports"
          className={`${baseClass} ${
            isReports
              ? "bg-purple-500 text-white"
              : "bg-purple-100 hover:bg-purple-200 text-purple-600"
          }`}
          title={locale === "es" ? "Reportes" : "Reports"}
          onClick={closeMobileMenu}
        >
          <BarChart3 className="w-5 h-5" />
        </Link>

        <Link
          to="/daily-summaries"
          className={`${baseClass} ${
            isDailySummaries
              ? "bg-orange-500 text-white"
              : "bg-orange-100 hover:bg-orange-200 text-orange-600"
          }`}
          title={locale === "es" ? "Resúmenes Diarios" : "Daily summaries"}
          onClick={closeMobileMenu}
        >
          <Clock className="w-5 h-5" />
        </Link>

        <Link
          to="/cases"
          className={`${baseClass} ${
            isCases
              ? "bg-red-500 text-white"
              : "bg-red-100 hover:bg-red-200 text-red-600"
          }`}
          title={locale === "es" ? "Incidentes" : "Incidents"}
          onClick={closeMobileMenu}
        >
          <AlertCircle className="w-5 h-5" />
        </Link>

        <Link
          to="/documents"
          className={`${baseClass} ${
            isDocuments
              ? "bg-teal-500 text-white"
              : "bg-teal-100 hover:bg-teal-200 text-teal-700"
          }`}
          title={locale === "es" ? "Documentos" : "Documents"}
          onClick={closeMobileMenu}
        >
          <Files className="w-5 h-5" />
        </Link>

        <Link
          to="/knowledge"
          className={`${baseClass} ${
            isKnowledge
              ? "bg-amber-500 text-white"
              : "bg-amber-100 hover:bg-amber-200 text-amber-700"
          }`}
          title={locale === "es" ? "Conocimiento" : "Knowledge"}
          onClick={closeMobileMenu}
        >
          <BookOpen className="w-5 h-5" />
        </Link>

        <div className="relative">
          <Link
            to="/settings"
            className={`${baseClass} ${
              isSettings
                ? "bg-gray-700 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600"
            }`}
            title={locale === "es" ? "Configuración" : "Settings"}
            onClick={closeMobileMenu}
          >
            <Settings className="w-5 h-5" />
          </Link>
        </div>

        <div className="flex-1" />

        <UserMenu />
      </div>
    </>
  );
}
